import { useState } from "react";
import { Button } from "@/components/ui/button";
import { KeyRound, ShieldCheck, Smartphone, X } from "lucide-react";
import { useMfaFactors } from "@/hooks/use-mfa";
import { MfaEnrollDialog } from "./MfaEnrollDialog";
import { useAuth } from "@/contexts/AuthContext";

export function MfaPromptBanner() {
  const { verified, loading, refresh } = useMfaFactors();
  const { user } = useAuth();
  const [enroll, setEnroll] = useState<"totp" | "phone" | null>(null);
  // Dismissal lasts only for the current login; a new sign-in shows it again.
  const key = user ? `mfa_prompt_dismissed_${user.id}_${user.last_sign_in_at ?? ""}` : null;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const dismissed = !!key && (dismissedKey === key || sessionStorage.getItem(key) === "1");

  if (loading || dismissed || verified.length > 0) return null;

  const dismiss = () => {
    if (key) {
      sessionStorage.setItem(key, "1");
      setDismissedKey(key);
    }
  };

  return (
    <>
      <section className="relative mb-6 border border-primary/30 bg-primary/10 px-4 py-4 pr-12 md:px-5 md:pr-12" aria-labelledby="mfa-prompt-title">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={dismiss}
          className="absolute right-1 top-1 min-h-11 min-w-11 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss two-step verification reminder"
        >
          <X className="h-4 w-4" />
        </Button>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 gap-3">
            <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
            <div>
              <h2 id="mfa-prompt-title" className="font-semibold text-foreground">Protect your account</h2>
              <p className="mt-1 text-sm text-muted-foreground">Add two-step verification using an authenticator app or text message.</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <Button type="button" size="sm" onClick={() => setEnroll("totp")}>
              <KeyRound className="mr-2 h-4 w-4" />
              Authenticator app
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setEnroll("phone")}>
              <Smartphone className="mr-2 h-4 w-4" />
              Text message
            </Button>
          </div>
        </div>
      </section>
      {enroll && (
        <MfaEnrollDialog
          open
          type={enroll}
          onOpenChange={(open) => !open && setEnroll(null)}
          onDone={refresh}
        />
      )}
    </>
  );
}
